import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { CIVS } from '../../gameplay/civilisations';
import { kind } from '../../gameplay/registry';
import { AnimMode, AnimatedInstances, createAnimatedMaterial, setMaterialSky } from '../../engine/rendering/animatedMaterial';
import { fowUniforms } from '../../engine/rendering/fowShader';
import { buildBuildingGeometry } from '../../engine/rendering/models/buildingModels';
import { buildResourceGeometry } from '../../engine/rendering/models/natureModels';
import { buildUnitGeometry, civSkin } from '../../engine/rendering/models/unitModels';
import { LIGHTING, buildSky } from '../../engine/rendering/sky';
import { ParticleSystem } from '../../engine/rendering/particles';
import { terrainTextures } from '../../engine/rendering/textures';

/**
 * Animated 3D diorama behind the main menu, built from the in-game model library. Its own profile:
 * the full material set (metal, rim, ground shading) and two burning braziers whose flames glow
 * (additive halos) and throw a flickering pool of light on the ground. A bloom pass and real point
 * lights were tried and dropped: in software rendering they made the menu 2.7 times as expensive,
 * and these stand-ins look much the same for almost nothing.
 */
export function MenuScene() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current!;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    } catch {
      return;
    }
    renderer.setPixelRatio(Math.min(1.5, window.devicePixelRatio));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    fowUniforms.uFowEnabled.value = 0;
    const scene = new THREE.Scene();
    const L = LIGHTING.dusk;
    scene.add(buildSky(L));
    scene.fog = new THREE.Fog(L.fog, 30, 90);
    scene.add(new THREE.HemisphereLight(L.skyHorizon, L.ground, 1.0));
    const sun = new THREE.DirectionalLight(L.sun, 2.6);
    sun.position.copy(L.sunDir).multiplyScalar(30);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    Object.assign(sun.shadow.camera, { left: -16, right: 16, top: 16, bottom: -16 });
    scene.add(sun);
    const tex = terrainTextures(256).grass.clone();
    tex.repeat.set(12, 12);
    tex.needsUpdate = true;
    const groundGeo = new THREE.CircleGeometry(60, 64);
    groundGeo.rotateX(-Math.PI / 2);
    const ground = new THREE.Mesh(groundGeo, new THREE.MeshStandardMaterial({ map: tex, roughness: 1 }));
    ground.receiveShadow = true;
    scene.add(ground);
    const { material, depth } = createAnimatedMaterial({ surfaces: true, metals: true, groundAO: { height: 0.45, min: 0.6 }, rim: 0.12 });
    setMaterialSky(material, L);
    const civ = CIVS[Math.floor(Math.random() * CIVS.length)];
    const team = new THREE.Color(0x2f6fd8);
    const age = Math.floor(Math.random() * 5);
    const place = (key: string, x: number, z: number) => {
      const v = buildBuildingGeometry(kind(key).building!, civ, age);
      const inst = new AnimatedInstances(v.geometry, material, depth, 1);
      inst.begin();
      inst.push(x, 0, z, 0, 1, team, 0, AnimMode.Static, 0, v.height);
      inst.end();
      scene.add(inst.mesh);
    };
    place('townCenter', 0, 0);
    place('house', -4.5, 1.5);
    place('house', -4, -2.5);
    place('barracks', 4.5, -2);
    place('mill', 4, 3.5);
    place('farm', 4, 7.5);
    place('guardTower', -2.5, 5.5);
    const trees = new AnimatedInstances(buildResourceGeometry('tree', 1), material, depth, 64);
    const pines = new AnimatedInstances(buildResourceGeometry('pine', 1), material, depth, 64);
    trees.begin();
    pines.begin();
    for (let i = 0; i < 70; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = 11 + Math.random() * 14;
      (i % 3 ? trees : pines).push(Math.cos(a) * r, 0, Math.sin(a) * r, Math.random() * 6, 0.9 + Math.random() * 0.4, team, Math.random() * 6, AnimMode.Static, 0, 0);
    }
    trees.end();
    pines.end();
    scene.add(trees.mesh, pines.mesh);
    const unitDefs = ['villager', 'militia', 'spearman', 'archer', 'knight'];
    const walkers = unitDefs.map((k) => {
      const u = kind(k).unit!;
      const inst = new AnimatedInstances(buildUnitGeometry(u, { accent: civ.uniform, skin: civSkin(civ.key) }), material, depth, 8);
      scene.add(inst.mesh);
      return inst;
    });
    // Two braziers, by the tower and the mill: stone bowls with live flames and a warm light each.
    const fire = new ParticleSystem(1024);
    scene.add(fire.group);
    const braziers: THREE.Vector3[] = [new THREE.Vector3(-1.2, 0, 6.6), new THREE.Vector3(6.4, 0, 1.4)];
    const bowlGeo = new THREE.CylinderGeometry(0.28, 0.16, 0.22, 10).translate(0, 0.62, 0);
    const legGeo = new THREE.CylinderGeometry(0.06, 0.09, 0.55, 6).translate(0, 0.27, 0);
    const stone = new THREE.MeshStandardMaterial({ color: 0x6a6258, roughness: 0.9 });
    const embers = new THREE.MeshStandardMaterial({ color: 0x401000, emissive: 0xff6a20, emissiveIntensity: 2.2 });
    const poolMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: { uTime: fowUniforms.uTime },
      vertexShader: 'varying vec2 vP; void main(){ vP = position.xz / 2.5; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: `uniform float uTime; varying vec2 vP;
        void main(){
          float d = length(vP);
          float flicker = 0.85 + 0.1 * sin(uTime * 13.0) + 0.05 * sin(uTime * 29.0);
          float a = pow(max(0.0, 1.0 - d), 2.2) * 0.55 * flicker;
          gl_FragColor = vec4(vec3(1.0, 0.5, 0.18) * a, 1.0);
        }`,
    });
    for (const b of braziers) {
      const bowl = new THREE.Mesh(bowlGeo, stone);
      const leg = new THREE.Mesh(legGeo, stone);
      const coals = new THREE.Mesh(new THREE.CircleGeometry(0.24, 10).rotateX(-Math.PI / 2).translate(0, 0.72, 0), embers);
      for (const m of [bowl, leg, coals]) {
        m.position.copy(b);
        m.castShadow = m !== coals;
        scene.add(m);
      }
      // The firelight on the ground: an additive warm pool that flickers (a real point light costs
      // every pixel of the scene two more lights).
      const pool = new THREE.Mesh(new THREE.PlaneGeometry(5, 5).rotateX(-Math.PI / 2), poolMat);
      pool.position.set(b.x, 0.02, b.z);
      pool.renderOrder = 1;
      scene.add(pool);
    }
    const cam = new THREE.PerspectiveCamera(36, 1, 0.5, 2000);
    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      renderer.setSize(w, h, false);
      fire.setPixelScale(h * renderer.getPixelRatio() * 0.9);
      cam.aspect = w / Math.max(1, h);
      cam.updateProjectionMatrix();
    };
    resize();
    window.addEventListener('resize', resize);
    let raf = 0;
    const t0 = performance.now();
    let last = t0;
    const loop = () => {
      const now = performance.now();
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const t = (now - t0) / 1000;
      for (const b of braziers) {
        fire.burst('flame', b.x, 0.78, b.z, 1, 0.08, 0.5);
        fire.burst('fire', b.x, 0.85, b.z, 1, 0.1, 0.6);
        if (Math.random() < 0.2) fire.burst('glow', b.x, 0.95, b.z, 1, 0.05, 0.3);
        if (Math.random() < 0.05) fire.burst('spark', b.x, 1.0, b.z, 1, 0.4, 1.2);
      }
      fire.update(dt);
      fowUniforms.uTime.value = t;
      walkers.forEach((w, i) => {
        w.begin();
        for (let j = 0; j < 2; j++) {
          const a = t * 0.18 + i * 1.25 + j * Math.PI;
          const r = 7.5 + (i % 2) * 0.8;
          w.push(Math.cos(a) * r, 0, Math.sin(a) * r, -a, 1, team, i + j, AnimMode.Walk, 0, 1);
        }
        w.end();
      });
      const ca = t * 0.05;
      cam.position.set(Math.cos(ca) * 22, 10, Math.sin(ca) * 22);
      cam.lookAt(0, 1, 0);
      renderer.render(scene, cam);
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      fowUniforms.uFowEnabled.value = 1;
      renderer.dispose();
    };
  }, []);
  return <canvas ref={ref} className="menu-scene" />;
}
